import {useMutation, type UseMutationOptions} from '@github-ui/react-query'
import {
  parseJSONWithBetterErrors,
  StaleContentError,
  throwErrorsIfBadResponse,
  throwIfStaleResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {usePullRequestBasePath} from '../stores/layout-selectors'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {getCurrentUser} from '../components/comments/utils/get-author-filters'
import {reactFetch} from '@github-ui/verified-fetch'
import {setCommentQueryData} from '../page-data/loaders/use-conversation-comments'

export interface UpdateIssueCommentVariables {
  id: number
  newBody: string
  bodyVersion: string
}

export interface UpdateIssueCommentResponse {
  /** New Markdown body. */
  source: string
  /** New rendered HTML body. */
  body: SafeHTMLString
  /** New version ID, used for identifying stale edits. */
  newBodyVersion: string
  /**
   * Path to get HTML fragment for edit history.
   * @deprecated Obviously we don't want to use this in React land.
   */
  editUrl: string
}

export interface UpdateIssueCommentStaleResponse {
  stale: true
  updated_markdown: string
  updated_html: SafeHTMLString
  version: string
}

type InternalUpdateIssueCommentResponse =
  | UpdateIssueCommentResponse
  | UpdateIssueCommentStaleResponse
  | {error?: string}

/**
 * Mutation to edit an issue comment, automatically updating the corresponding item in the `conversation_comments`
 * query store.
 */
export function useUpdateIssueCommentMutation(
  options: Omit<UseMutationOptions<unknown, Error, UpdateIssueCommentVariables, unknown>, 'mutationFn'> = {},
) {
  const basePath = usePullRequestBasePath()
  const {repository} = useLayoutRouteData()

  return useMutation({
    ...options,
    mutationFn: async ({id, newBody, bodyVersion}: UpdateIssueCommentVariables) => {
      // Comment editing works repo-wide across PRs and issues with unique comment IDs; it's not scoped to just this PR
      const path = `/${encodeURIComponent(repository.ownerLogin)}/${encodeURIComponent(repository.name)}/issue_comments/${id}`

      const body = new FormData()
      body.set('issue_comment[body]', newBody)
      body.set('body_version', bodyVersion)

      const response = await reactFetch(path, {
        method: 'PUT',
        body,
        headers: {
          Accept: 'application/json',
        },
      })

      const json: InternalUpdateIssueCommentResponse = await parseJSONWithBetterErrors(response)

      throwIfStaleResponse(response, json)
      throwErrorsIfBadResponse(response, json)

      return json as UpdateIssueCommentResponse
    },
    onError: async (error, variables, onMutateResult, context) => {
      if (error instanceof StaleContentError) {
        const freshData = (error as StaleContentError<UpdateIssueCommentStaleResponse>).freshData
        setCommentQueryData(context.client, basePath, variables.id, comment => {
          comment.body = freshData.updated_markdown
          comment.bodyHTML = freshData.updated_html
          comment.bodyVersion = freshData.version
          // ideally we'd set lastUserContentEdit too, but we don't have that data
        })
      }

      return options.onError?.(error, variables, onMutateResult, context)
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      const editor = getCurrentUser() ?? null

      setCommentQueryData(context.client, basePath, variables.id, comment => {
        const hasFreshEditMetadata = comment.bodyVersion === response.newBodyVersion
        comment.body = response.source
        comment.bodyHTML = response.body
        comment.bodyVersion = response.newBodyVersion
        if (!hasFreshEditMetadata) {
          comment.lastUserContentEdit = {
            editedAt: null,
            editor,
            id: null,
          }
        }
      })

      return options.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
